import React from 'react';

import Textarea from '@/components/input/Textarea';
import { ContentType } from '@/services/contents/types';
import QuizEditor from '@/components/interactive/Quiz/QuizEditor';
import theme from '@/constants/theme';

import { SubmitFile } from './SubmitFile';

interface Props {
  setType: React.Dispatch<React.SetStateAction<ContentType>>;
  type: ContentType;
  setContent: React.Dispatch<React.SetStateAction<string>>;
  content: string;
  setFile: React.Dispatch<React.SetStateAction<string | File | null>>;
  file: string | File | null;
  setSubtitles: React.Dispatch<React.SetStateAction<string | null>>;
  subtitles: string | null;
  subtitlesError: boolean;
  quizData: string | null;
  setQuizData: React.Dispatch<React.SetStateAction<string | null>>;
  setDuration: React.Dispatch<React.SetStateAction<number | string>>;
  analysisPrompt: string | null;
  setAnalysisPrompt: React.Dispatch<React.SetStateAction<string | null>>;
}

const ContentTypeSelector: React.FC<Props> = ({
  setContent,
  content,
  setFile,
  file,
  setSubtitles,
  subtitles,
  setType,
  type,
  subtitlesError,
  quizData,
  setQuizData,
  setDuration,
  analysisPrompt,
  setAnalysisPrompt,
}) => {
  const contentTypeOptions: { title: string; option: ContentType }[] = [
    {
      title: 'Lectura',
      option: 'READING',
    },
    {
      title: 'Audio',
      option: 'AUDIO',
    },
    {
      title: 'Video',
      option: 'VIDEO',
    },
    {
      title: 'Imagen',
      option: 'IMAGE',
    },
    {
      title: 'Cuestionario',
      option: 'QUIZ',
    },
  ];

  const handleChangeType = (e: React.ChangeEvent<HTMLInputElement>) => {
    setType(e.target.value as ContentType);
    setFile(null);
    setContent('');
    setQuizData(null);
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <p style={{ color: theme.colors.text.body }}>Tipo de contenido</p>
        <small style={{ color: theme.colors.text.caption }}>
          Determina el tipo de contenido que se podrá subir y mostrar a los usuarios.
        </small>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {contentTypeOptions.map(({ title, option }) => (
          <label
            key={option}
            style={{
              display: 'flex',
              gap: 8,
              color: theme.colors.text.body,
              padding: 16,
              borderRadius: 8,
              cursor: 'pointer',
              border: `1px solid ${theme.colors.border}`,
              accentColor: theme.colors.primary,
            }}
          >
            <input
              type="radio"
              name="type"
              value={option}
              checked={type === option}
              onChange={handleChangeType}
            />
            <span style={{ color: option === type ? theme.colors.primary : theme.colors.text.body }}>
              {title}
            </span>
          </label>
        ))}
      </div>
      {(['READING', 'IMAGE'] as ContentType[]).includes(type) && (
        <Textarea
          name="content"
          required={type === 'READING'}
          placeholder="Content"
          value={content}
          label="Contenido"
          helperText="Escribe el texto que deseas mostrar a los usuarios, puedes usar Markdown."
          onChange={(e) => setContent(e.target.value)}
        />
      )}
      <SubmitFile file={file} setFile={setFile} contentType={type} setDuration={setDuration} />
      {(['AUDIO'] as ContentType[]).includes(type) && (
        <div>
          <Textarea
            name="subtitles"
            placeholder="Subtitles"
            value={subtitles ?? ''}
            label="Subtitulos"
            helperText="Agrega aqui los subtitulos en formato de json [{text, start, end},...]"
            onChange={(e) => setSubtitles(e.target.value)}
          />
          {subtitlesError && (
            <div style={{ color: 'red' }}>
              Los subtitulos estan en formato invalido, deben ser una lista de text, end, start
            </div>
          )}
        </div>
      )}
      {(['QUIZ'] as ContentType[]).includes(type) && (
        <>
          <Textarea
            label="Prompt de Análisis"
            helperText="Este prompt se usará como contexto para analizar las respuestas de los usuarios y así brindar respuestas más personalizadas."
            placeholder='Eres un experto en "X" y resolves un problema...'
            name="analysisPrompt"
            value={analysisPrompt ?? ''}
            onChange={(e) => setAnalysisPrompt(e.target.value)}
          />
          <QuizEditor quizData={quizData} setQuizData={setQuizData} />
        </>
      )}
    </>
  );
};

export default ContentTypeSelector;
